// src/stores/authStore.ts
// Zustand store managing authentication state for the SGS Connect admin dashboard.
// Listens to Supabase auth state changes and keeps profile data in sync.

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { ProfileRow, UserRole } from '@/lib/database.types'

// ── Types ─────────────────────────────────────────────────────

interface AuthState {
  /** Supabase auth user object */
  user: User | null
  /** Full profile row from the profiles table */
  profile: ProfileRow | null
  /** Current auth session */
  session: Session | null
  /** True while auth state is being determined on initial load */
  loading: boolean
  /** True while a sign-in/sign-out operation is in progress */
  authLoading: boolean
  /** Any auth error message */
  error: string | null
}

interface AuthActions {
  /** Initialize auth: load session and set up the auth listener */
  initialize: () => Promise<void>
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<void>
  /** Sign in with Google OAuth */
  signInWithGoogle: () => Promise<void>
  /** Sign out the current user */
  signOut: () => Promise<void>
  /** Fetch the profile row for the currently authenticated user */
  fetchProfile: (userId: string) => Promise<void>
  /** Update the profile in the store (after a successful update mutation) */
  setProfile: (profile: ProfileRow) => void
  /** Clear any auth error */
  clearError: () => void
  /** Check if the current user has a specific role or above */
  hasRole: (role: UserRole) => boolean
  /** Check if the current user is an admin or pastor */
  isAdmin: () => boolean
}

type AuthStore = AuthState & AuthActions

// ── Role hierarchy ─────────────────────────────────────────────
const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 4,
  pastor: 3,
  leader: 2,
  member: 1,
}

// ── Store ─────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // ── Initial State ─────────────────────────────────────
      user: null,
      profile: null,
      session: null,
      loading: true,
      authLoading: false,
      error: null,

      // ── Actions ───────────────────────────────────────────

      initialize: async () => {
        try {
          // Get initial session
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user) {
            set({ user: session.user, session, loading: true })
            await get().fetchProfile(session.user.id)
          } else {
            set({ loading: false })
          }

          // Listen for auth state changes (sign in, sign out, token refresh)
          supabase.auth.onAuthStateChange(async (event, session) => {
            if (session?.user) {
              set({ user: session.user, session })

              // Fetch profile on sign-in and token refresh
              if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                await get().fetchProfile(session.user.id)
              }
            } else {
              // Signed out — clear all state
              set({
                user: null,
                profile: null,
                session: null,
                loading: false,
              })
            }
          })
        } catch (error) {
          console.error('Auth initialization error:', error)
          set({ loading: false })
        }
      },

      signIn: async (email: string, password: string) => {
        set({ authLoading: true, error: null })
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email: email.trim().toLowerCase(),
            password,
          })

          if (error) throw error

          if (data.user) {
            await get().fetchProfile(data.user.id)
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed'
          set({ error: message })
          throw error
        } finally {
          set({ authLoading: false })
        }
      },

      signInWithGoogle: async () => {
        set({ authLoading: true, error: null })
        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
              queryParams: {
                access_type: 'offline',
                prompt: 'consent',
              },
            },
          })
          if (error) throw error
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google sign in failed'
          set({ error: message, authLoading: false })
          throw error
        }
        // authLoading stays true during redirect
      },

      signOut: async () => {
        set({ authLoading: true, error: null })
        try {
          const { error } = await supabase.auth.signOut()
          if (error) throw error
          set({ user: null, profile: null, session: null })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign out failed'
          set({ error: message })
          throw error
        } finally {
          set({ authLoading: false })
        }
      },

      fetchProfile: async (userId: string) => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single()

          if (error) {
            // Profile might not exist yet (race condition with trigger)
            if (error.code === 'PGRST116') {
              // Row not found — wait briefly and retry once
              await new Promise((resolve) => setTimeout(resolve, 1000))
              const { data: retryData, error: retryError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single()

              if (!retryError && retryData) {
                set({ profile: retryData, loading: false })
                return
              }
            }
            console.error('Error fetching profile:', error)
            set({ loading: false })
            return
          }

          set({ profile: data, loading: false })
        } catch (error) {
          console.error('fetchProfile error:', error)
          set({ loading: false })
        }
      },

      setProfile: (profile: ProfileRow) => {
        set({ profile })
      },

      clearError: () => {
        set({ error: null })
      },

      hasRole: (requiredRole: UserRole) => {
        const { profile } = get()
        if (!profile) return false
        return ROLE_HIERARCHY[profile.role] >= ROLE_HIERARCHY[requiredRole]
      },

      isAdmin: () => {
        const { profile } = get()
        if (!profile) return false
        return profile.role === 'admin' || profile.role === 'pastor'
      },
    }),
    { name: 'sgs-auth-store' }
  )
)

// ── Selectors (for performance — avoid re-renders) ────────────

export const selectUser = (state: AuthStore) => state.user
export const selectProfile = (state: AuthStore) => state.profile
export const selectIsLoading = (state: AuthStore) => state.loading
export const selectIsAuthLoading = (state: AuthStore) => state.authLoading
export const selectIsAdmin = (state: AuthStore) => state.isAdmin()
export const selectError = (state: AuthStore) => state.error
